import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { getImageUrl } from '../utils/imageUtils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/ui/password-input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Upload, Trash2, Save, UserPlus, MessageSquare, X, Copy } from 'lucide-react';
import NotificationSettings from '../components/NotificationSettings';

const ProfilePage = ({ user, setUser, isPublicView }) => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [viewingUser, setViewingUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [profileData, setProfileData] = useState({
    display_name: user?.display_name || '',
    bio: user?.bio || '',
    gender: user?.gender || '',
    age: user?.age || '',
    avatar_url: user?.avatar_url || '',
    show_online_status: user?.show_online_status !== false  // Default to true
  });
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [uploading, setUploading] = useState(false);
  const [defaultAvatars, setDefaultAvatars] = useState({
    male: [],
    female: [],
    other: []
  });
  const [coinWallet, setCoinWallet] = useState({ coins: 0, daily_reward: 100, can_claim: false });
  const [loadingWallet, setLoadingWallet] = useState(false);
  const [adminConnections, setAdminConnections] = useState({ friends: [], blocked_users: [] });
  const INVITE_BIO_REGEX = /(https?:\/\/[^\s]*\/join\/[A-Za-z0-9_-]+|\/join\/[A-Za-z0-9_-]+|\bjoin\s+[A-Za-z0-9_-]{4,})/gi;

  useEffect(() => {
    if (isPublicView && userId) {
      fetchUserProfile();
    }
    loadDefaultAvatars();
  }, [isPublicView, userId]);

  useEffect(() => {
    if (!isPublicView && user?.id) {
      loadCoinWallet();
    }
  }, [isPublicView, user?.id]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setShowAvatarModal(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const loadDefaultAvatars = async () => {
    try {
      const [maleRes, femaleRes] = await Promise.all([
        axios.get(`${API}/admin/avatars/male`),
        axios.get(`${API}/admin/avatars/female`)
      ]);
      setDefaultAvatars({
        male: maleRes.data,
        female: femaleRes.data,
        other: []  // Can add other avatars if needed
      });
    } catch (error) {
      console.error('Failed to load default avatars:', error);
      // Set fallback avatars if API fails
      setDefaultAvatars({
        male: [{ avatar_url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=male1' }],
        female: [{ avatar_url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=female1' }],
        other: [{ avatar_url: 'https://api.dicebear.com/7.x/bottts/svg?seed=other1' }]
      });
    }
  };

  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      let profile = response.data;
      if (user?.role === 'super_admin') {
        try {
          const connectionsRes = await axios.get(`${API}/admin/users/${userId}/connections`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setAdminConnections({
            friends: connectionsRes.data?.friends || [],
            blocked_users: connectionsRes.data?.blocked_users || []
          });
          profile = {
            ...profile,
            friends: connectionsRes.data?.friends || profile.friends || [],
            blocked_users: connectionsRes.data?.blocked_users || profile.blocked_users || []
          };
        } catch {
          setAdminConnections({
            friends: profile.friends || [],
            blocked_users: profile.blocked_users || []
          });
        }
      }
      setViewingUser(profile);
    } catch (error) {
      toast.error('Failed to load user profile');
      navigate('/chat');
    }
  };

  const loadCoinWallet = async () => {
    try {
      setLoadingWallet(true);
      const response = await axios.get(`${API}/coins/wallet`);
      setCoinWallet(response.data || { coins: 0, daily_reward: 100, can_claim: false });
    } catch {
      setCoinWallet({ coins: Number(user?.coins || 0), daily_reward: 100, can_claim: false });
    } finally {
      setLoadingWallet(false);
    }
  };

  const claimDailyCoins = async () => {
    try {
      await axios.post(`${API}/coins/claim-daily`);
      toast.success('Daily coins claimed');
      loadCoinWallet();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to claim daily reward');
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Prepare data with proper type conversions
      const dataToSend = {
        display_name: profileData.display_name || '',
        bio: profileData.bio || '',
        gender: profileData.gender || null,
        age: profileData.age && profileData.age !== '' ? parseInt(profileData.age, 10) : null,
        avatar_url: profileData.avatar_url || null,
      };
      
      // Remove null values to avoid sending them
      Object.keys(dataToSend).forEach(key => {
        if (dataToSend[key] === null || dataToSend[key] === '') {
          delete dataToSend[key];
        }
      });
      
      const response = await axios.put(`${API}/users/profile`, dataToSend);
      setUser(response.data);
      toast.success('Profile updated successfully!');
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMsg = typeof errorDetail === 'string' 
        ? errorDetail 
        : Array.isArray(errorDetail) 
          ? errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ')
          : 'Failed to update profile';
      toast.error(errorMsg);
      console.error('Profile update error:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };


  const handleOnlineStatusToggle = async (newValue) => {
    try {
      const response = await axios.put(`${API}/user/settings/online-status?show_status=${newValue}`);
      setProfileData({...profileData, show_online_status: newValue});
      setUser({...user, show_online_status: newValue});
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update online status setting');
    }
  };


  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    try {
      setLoading(true);
      await axios.put(`${API}/users/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });
      toast.success('Password changed successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/media/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const avatarPath = response.data.url;
      setProfileData({ ...profileData, avatar_url: avatarPath });
      toast.success('Avatar uploaded successfully!');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to upload avatar';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Failed to upload avatar');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== user.username) {
      toast.error('Username does not match');
      return;
    }

    try {
      setLoading(true);
      await axios.delete(`${API}/users/account`);
      localStorage.removeItem('token');
      toast.success('Account deleted successfully');
      navigate('/auth');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setLoading(false);
      setShowDeleteDialog(false);
    }
  };

  const displayUser = isPublicView && viewingUser ? viewingUser : user;
  const isOwnProfile = !isPublicView || !viewingUser;

  const renderBioWithClickableInvites = (bioText) => {
    const text = String(bioText || '');
    if (!text) return null;

    return text.split(INVITE_BIO_REGEX).map((part, idx) => {
      if (!part) return null;
      const directJoin = part.match(/\/join\/([A-Za-z0-9_-]+)/i);
      const joinWord = part.match(/\bjoin\s+([A-Za-z0-9_-]{4,})/i);
      const inviteCode = directJoin?.[1] || joinWord?.[1] || null;

      if (inviteCode) {
        return (
          <button
            key={`bio-link-${idx}`}
            type="button"
            className="text-blue-600 dark:text-blue-400 underline underline-offset-2 hover:text-blue-700 dark:hover:text-blue-300"
            onClick={() => navigate(`/join/${inviteCode}`)}
            title={`Join room with code ${inviteCode}`}
          >
            {part}
          </button>
        );
      }

      return <span key={`bio-text-${idx}`}>{part}</span>;
    });
  };

  if (isPublicView && !viewingUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <Button
          variant="ghost"
          onClick={() => {
            // Check if user came from a specific room
            const returnToRoom = sessionStorage.getItem('returnToRoom');
            if (returnToRoom) {
              sessionStorage.removeItem('returnToRoom');
              navigate('/chat', { state: { roomId: returnToRoom } });
            } else {
              navigate(-1);
            }
          }}
          className="mb-6 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                {isOwnProfile ? 'Profile Settings' : `${displayUser.username}'s Profile`}
              </h1>
              {displayUser.rank && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gradient-to-r from-yellow-400 to-orange-500 text-white">
                    ⭐ Rank {displayUser.rank}
                  </span>
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    {displayUser.rank_points || 0} points • {displayUser.total_messages || 0} messages
                  </span>
                </div>
              )}
            </div>
            
            {/* Action buttons for public profile */}
            {!isOwnProfile && (
              <div className="flex gap-2">
                <Button
                  variant="default"
                  size="icon"
                  title="Add Friend"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      await axios.post(`${API}/friends/request/${displayUser.id}`, {}, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      toast.success('Friend request sent!');
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Failed to send friend request');
                    }
                  }}
                >
                  <UserPlus className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  title="Send Message"
                  onClick={() => {
                    // Navigate to chat with PM state to open PM with this user
                    navigate('/chat', { 
                      state: { 
                        openPM: true, 
                        pmUserId: displayUser.id,
                        pmUsername: displayUser.username 
                      } 
                    });
                  }}
                >
                  <MessageSquare className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>

          {/* Badges Section */}
          {displayUser.badge_details && displayUser.badge_details.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4 dark:text-gray-200">Badges</h2>
              <div className="flex flex-wrap gap-3">
                {displayUser.badge_details.map((badge, index) => (
                  <div
                    key={badge.id || index}
                    className="group relative"
                    title={badge.name}
                  >
                    {badge.emoji_sequence ? (
                      <div className="w-12 h-12 rounded-lg border-2 border-yellow-400 dark:border-yellow-600 hover:scale-110 transition-transform cursor-pointer shadow-md flex items-center justify-center bg-white dark:bg-gray-800 text-2xl">
                        {badge.emoji_sequence}
                      </div>
                    ) : badge.icon_url ? (
                      <img
                        src={getImageUrl(badge.icon_url)}
                        alt={badge.name}
                        className="w-12 h-12 rounded-lg border-2 border-yellow-400 dark:border-yellow-600 hover:scale-110 transition-transform cursor-pointer shadow-md"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg border-2 border-yellow-400 dark:border-yellow-600 hover:scale-110 transition-transform cursor-pointer shadow-md flex items-center justify-center bg-white dark:bg-gray-800 text-sm font-bold">
                        {badge.name?.substring(0, 2).toUpperCase()}
                      </div>
                    )}
                    {/* Tooltip */}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                      <div className="font-semibold">{badge.name}</div>
                      {badge.description && (
                        <div className="text-xs text-gray-300 mt-1">{badge.description}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Avatar Section */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4 dark:text-gray-200">Profile Picture</h2>
            <div className="flex items-center gap-6">
              <Avatar 
                className="w-24 h-24 cursor-pointer hover:ring-2 hover:ring-blue-500 dark:hover:ring-blue-400 transition-all" 
                onClick={() => setShowAvatarModal(true)}
                title="Click to view full size"
              >
                <AvatarImage src={getImageUrl(isOwnProfile ? profileData.avatar_url : displayUser.avatar_url)} />
                <AvatarFallback className="dark:bg-gray-600 dark:text-gray-200">{displayUser?.username?.charAt(0).toUpperCase()}</AvatarFallback>
              </Avatar>
              {isOwnProfile && (
                <div className="space-y-2">
                  <label htmlFor="avatar-upload" className="cursor-pointer">
                    <Button asChild disabled={uploading} className="dark:bg-blue-600 dark:hover:bg-blue-700 dark:text-white">
                      <span>
                        <Upload className="w-4 h-4 mr-2" />
                        {uploading ? 'Uploading...' : 'Upload Image'}
                      </span>
                    </Button>
                  </label>
                  <input
                    id="avatar-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarUpload}
                    className="hidden"
                  />
                  <p className="text-sm text-gray-500 dark:text-gray-400">Max 10MB, JPG/PNG</p>
                </div>
              )}
            </div>

            {/* Default Avatars - Only show for own profile */}
            {isOwnProfile && profileData.gender && (
              <div className="mt-4">
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">Or choose a default avatar:</p>
                <div className="flex gap-2 flex-wrap">
                  {defaultAvatars[profileData.gender]?.map((avatar, idx) => (
                    <img
                      key={idx}
                      src={getImageUrl(avatar.avatar_url)}
                      alt={`Avatar ${idx + 1}`}
                      className="w-12 h-12 rounded-full cursor-pointer hover:ring-2 ring-blue-500 dark:ring-blue-400"
                      onClick={() => setProfileData({ ...profileData, avatar_url: avatar.avatar_url })}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Profile Info Form */}
          <form onSubmit={isOwnProfile ? handleProfileUpdate : (e) => e.preventDefault()} className="space-y-6 mb-8">
            <div className="border rounded-lg p-4 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm text-amber-700 dark:text-amber-300 font-medium">Coin Wallet</p>
                  <p className="text-2xl font-bold text-amber-800 dark:text-amber-200">
                    {isOwnProfile ? (loadingWallet ? '...' : (coinWallet.coins ?? user?.coins ?? 0)) : (displayUser.coins ?? 0)}
                  </p>
                  {isOwnProfile && (
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                      Daily reward: {coinWallet.daily_reward || 100} coins
                    </p>
                  )}
                </div>
                {isOwnProfile && (
                  <Button
                    type="button"
                    variant={coinWallet.can_claim ? 'default' : 'outline'}
                    onClick={claimDailyCoins}
                    disabled={!coinWallet.can_claim}
                    className={coinWallet.can_claim ? 'bg-amber-600 hover:bg-amber-700' : ''}
                  >
                    {coinWallet.can_claim ? 'Claim Daily' : 'Already Claimed'}
                  </Button>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="display_name" className="dark:text-gray-200">Display Name</Label>
              <Input
                id="display_name"
                value={isOwnProfile ? profileData.display_name : (displayUser.display_name || '')}
                onChange={isOwnProfile ? (e) => setProfileData({ ...profileData, display_name: e.target.value }) : undefined}
                disabled={!isOwnProfile}
                className={!isOwnProfile ? 'bg-gray-100 dark:bg-gray-700 dark:text-gray-200' : 'dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600'}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label htmlFor="bio" className="dark:text-gray-200">Bio</Label>
                {!isOwnProfile && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(displayUser.bio || '');
                        toast.success('Bio copied');
                      } catch {
                        toast.error('Unable to copy bio');
                      }
                    }}
                  >
                    <Copy className="w-3 h-3 mr-1" />
                    Copy
                  </Button>
                )}
              </div>
              {isOwnProfile ? (
                <Textarea
                  id="bio"
                  value={profileData.bio}
                  onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                  placeholder="Tell us about yourself..."
                  rows={4}
                  className="dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:placeholder-gray-400"
                />
              ) : (
                <div
                  id="bio"
                  className="min-h-[96px] w-full rounded-md border border-input bg-gray-100 dark:bg-gray-700 dark:text-gray-200 px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap break-words select-text"
                >
                  {renderBioWithClickableInvites(displayUser.bio)}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="gender" className="dark:text-gray-200">Gender</Label>
                {isOwnProfile ? (
                  <Select
                    value={profileData.gender}
                    onValueChange={(value) => setProfileData({ ...profileData, gender: value })}
                  >
                    <SelectTrigger className="dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600">
                      <SelectItem value="male" className="dark:hover:bg-gray-600">Male</SelectItem>
                      <SelectItem value="female" className="dark:hover:bg-gray-600">Female</SelectItem>
                      <SelectItem value="other" className="dark:hover:bg-gray-600">Other</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={displayUser.gender || 'Not specified'}
                    disabled
                    className="bg-gray-100 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
                  />
                )}
              </div>

              <div>
                <Label htmlFor="age" className="dark:text-gray-200">Age</Label>
                <Input
                  id="age"
                  type="number"
                  min="13"
                  max="120"
                  value={isOwnProfile ? profileData.age : (displayUser.age || '')}
                  onChange={isOwnProfile ? (e) => setProfileData({ ...profileData, age: e.target.value }) : undefined}
                  disabled={!isOwnProfile}
                  className={!isOwnProfile ? 'bg-gray-100 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600' : 'dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600'}
                />
              </div>
            </div>

            {/* Dark Mode Toggle */}
            {isOwnProfile && (
              <div className="border-t dark:border-gray-700 pt-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div>
                    <Label className="text-base font-semibold dark:text-gray-200">Dark Mode</Label>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Enable dark theme across the application</p>
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const newDarkMode = !user.dark_mode;
                        const token = localStorage.getItem('token');
                        await axios.put(`${API}/users/dark-mode?dark_mode=${newDarkMode}`, {}, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        setUser({...user, dark_mode: newDarkMode});
                        
                        // Store dark mode preference for THIS user specifically
                        const userDarkModeKey = `darkMode_${user.id}`;
                        localStorage.setItem(userDarkModeKey, newDarkMode.toString());
                        
                        toast.success(`Dark mode ${newDarkMode ? 'enabled' : 'disabled'}`);
                        // Apply dark mode class to document
                        if (newDarkMode) {
                          document.documentElement.classList.add('dark');
                        } else {
                          document.documentElement.classList.remove('dark');
                        }
                      } catch (error) {
                        toast.error('Failed to toggle dark mode');
                      }
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      user?.dark_mode ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        user?.dark_mode ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                
                {/* Accept Private Messages Toggle */}
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg mt-4">
                  <div>
                    <Label className="text-base font-semibold dark:text-gray-200">Accept Private Messages</Label>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Allow other users to send you PM requests</p>
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const newAcceptPms = !user.accept_pms;
                        const token = localStorage.getItem('token');
                        await axios.put(`${API}/users/settings`, {
                          accept_pms: newAcceptPms
                        }, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        setUser({...user, accept_pms: newAcceptPms});
                        toast.success(`PM requests ${newAcceptPms ? 'enabled' : 'disabled'}`);
                      } catch (error) {
                        toast.error('Failed to update PM settings');
                      }
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      user?.accept_pms !== false ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        user?.accept_pms !== false ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>


                {/* Online Status Toggle (Premium, Staff, Super Admin only) */}
                {(user?.role === 'premium' || user?.role === 'staff' || user?.role === 'super_admin') && (
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900 dark:text-gray-100">Show Online Status</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-300">Display your online status to other users. When off, you appear offline.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleOnlineStatusToggle(!profileData.show_online_status)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        profileData.show_online_status !== false ? 'bg-green-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          profileData.show_online_status !== false ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                )}

              </div>
            )}

            {isOwnProfile && (
              <Button type="submit" disabled={loading} className="dark:bg-blue-600 dark:hover:bg-blue-700 dark:text-white">
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Saving...' : 'Save Profile'}
              </Button>
            )}
          </form>

          {/* Admin View - Extra Information */}
          {!isOwnProfile && viewingUser && (user?.role === 'super_admin' || viewingUser.friends || viewingUser.blocked_users) && (
            <div className="border-t dark:border-gray-700 pt-8 mb-8 space-y-6">
              <h2 className="text-xl font-semibold mb-4 dark:text-gray-200">Admin Information</h2>
              
              {/* Email */}
              <div>
                <Label className="dark:text-gray-200">Email</Label>
                <Input value={viewingUser.email || 'Not provided'} disabled className="bg-gray-100 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" />
              </div>

              {/* Last Login */}
              {viewingUser.last_login && (
                <div>
                  <Label className="dark:text-gray-200">Last Login</Label>
                  <Input 
                    value={new Date(viewingUser.last_login).toLocaleString()} 
                    disabled 
                    className="bg-gray-100 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" 
                  />
                </div>
              )}

              {/* Friends List */}
              {(viewingUser.friends?.length > 0 || adminConnections.friends?.length > 0) && (
                <div>
                  <Label className="mb-2 block dark:text-gray-200">Friends ({(viewingUser.friends || adminConnections.friends || []).length})</Label>
                  <div className="border dark:border-gray-600 rounded-lg p-3 bg-gray-50 dark:bg-gray-700 max-h-40 overflow-y-auto">
                    <div className="space-y-2">
                      {(viewingUser.friends || adminConnections.friends || []).map((friend) => (
                        <div key={friend.id} className="flex items-center gap-2 text-sm dark:text-gray-200">
                          <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs">
                            {(friend.display_name || friend.username)?.charAt(0).toUpperCase()}
                          </div>
                          <span>{friend.display_name || friend.username}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Blocked Users */}
              {(viewingUser.blocked_users?.length > 0 || adminConnections.blocked_users?.length > 0) && (
                <div>
                  <Label className="mb-2 block dark:text-gray-200">Blocked Users ({(viewingUser.blocked_users || adminConnections.blocked_users || []).length})</Label>
                  <div className="border dark:border-gray-600 rounded-lg p-3 bg-red-50 dark:bg-red-900/20 max-h-40 overflow-y-auto">
                    <div className="space-y-2">
                      {(viewingUser.blocked_users || adminConnections.blocked_users || []).map((blocked) => (
                        <div key={blocked.id} className="flex items-center gap-2 text-sm dark:text-gray-200">
                          <div className="w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center text-xs">
                            {(blocked.display_name || blocked.username)?.charAt(0).toUpperCase()}
                          </div>
                          <span>{blocked.display_name || blocked.username}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Notification Settings Section - Only for own profile */}
          {isOwnProfile && (
            <div className="border-t dark:border-gray-700 pt-8 mb-8">
              <h2 className="text-xl font-semibold mb-4 dark:text-gray-200">Notifications</h2>
              <div className="max-w-md">
                <NotificationSettings />
              </div>
            </div>
          )}

          {/* Change Password Section - Only for own profile */}
          {isOwnProfile && (
            <div className="border-t dark:border-gray-700 pt-8 mb-8">
            <h2 className="text-xl font-semibold mb-4 dark:text-gray-200">Change Password</h2>
            <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
              <div>
                <Label htmlFor="current_password" className="dark:text-gray-200">Current Password</Label>
                <PasswordInput
                  id="current_password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  required
                  className="dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
                />
              </div>
              <div>
                <Label htmlFor="new_password" className="dark:text-gray-200">New Password</Label>
                <PasswordInput
                  id="new_password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  required
                  className="dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
                />
              </div>
              <div>
                <Label htmlFor="confirm_password" className="dark:text-gray-200">Confirm New Password</Label>
                <PasswordInput
                  id="confirm_password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  required
                  className="dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
                />
              </div>
              <Button type="submit" variant="outline" disabled={loading} className="dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600">
                Change Password
              </Button>
            </form>
            </div>
          )}

          {/* Delete Account Section - Only for own profile */}
          {isOwnProfile && (
            <div className="border-t dark:border-gray-700 pt-8">
              <h2 className="text-xl font-semibold mb-4 text-red-600 dark:text-red-400">Danger Zone</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Once you delete your account, there is no going back. Please be certain.
              </p>
              <Button
                variant="destructive"
                onClick={() => setShowDeleteDialog(true)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Account
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Account</DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete your account
              and remove all your data from our servers.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Please type <span className="font-semibold">{user?.username}</span> to confirm.
            </p>
            <Input
              placeholder="Enter your username"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={deleteConfirm !== user?.username || loading}
            >
              {loading ? 'Deleting...' : 'Delete My Account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Avatar Full View Modal */}
      {showAvatarModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={() => setShowAvatarModal(false)}
        >
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <img
              src={getImageUrl(isOwnProfile ? profileData.avatar_url : displayUser.avatar_url)}
              alt="Profile Picture"
              className="max-w-[90vw] max-h-[90vh] rounded-lg object-contain"
            />
            <button
              onClick={() => setShowAvatarModal(false)}
              className="absolute top-2 right-2 bg-gray-800 rounded-full p-1 text-white hover:bg-gray-700 transition-colors"
              aria-label="Close image viewer"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
